import React, { useState, useEffect, useCallback, useContext } from 'react';
import { View, Platform, Linking, Text, ScrollView, StatusBar, Dimensions, TouchableOpacity, Image, StyleSheet, SafeAreaView } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import Modal from './Modal'; // Assuming Modal is custom
import { fetchAuthSession } from 'aws-amplify/auth'; // Mocked import for session fetch
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from './App.web'; 
import * as FileSystem from 'expo-file-system';
import { apiUrl } from './utils/config';
import { useStory } from './StoryContext';
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { LoadingContext } from './LoadingContext';
import * as Font from 'expo-font';
import PurchaseStoryModal from './PurchaseStoryModal';
import ParentalGateModal from './ParentalGateModal';


const { width: screenWidth } = Dimensions.get('window'); // Get screen width

interface Story {
  story_id: string;
  title: string;
  description: string;
  image_url: string;
  chapters: number;
  prompt_number: number;
  character_selected?: boolean;
  category: string;
  tags?: string[];
  status: string;
  session_id?: string;
  image_key: string;  first_prompt: string;
  images: boolean;
  items: object[];
  objective: string;
}

type NavigationProp = StackNavigationProp<RootStackParamList>;

const StoriesPage: React.FC = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [inProgressStories, setInProgressStories] = useState<Story[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [tokens, setTokens] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { loading, setLoading } = useContext(LoadingContext);
  const [isSelectingStoryForUpdate, setIsSelectingStoryForUpdate] = useState(false);
  const [showStorySelectionModal, setShowStorySelectionModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showParentalGate, setShowParentalGate] = useState(false);
  const [categoryDropdownVisible, setCategoryDropdownVisible] = useState(false);
  type GatePurpose = 'purchase_pdf' | 'subscription';

  const [gatePurpose, setGatePurpose] = useState<GatePurpose | null>(null);


  const handleToggleCategoryDropdown = () => {
    setCategoryDropdownVisible((prev) => !prev);
  };

  const { setContext, resetContext } = useStory();
  const navigation = useNavigation<NavigationProp>();

  const loadFonts = async () => {
      await Font.loadAsync({
        'Futura': require('./assets/fonts/futura.ttf'),
      });
    };
  
    useEffect(() => {
      loadFonts();
    }, []);

  // Fetch the auth token
  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken;
      const groups = session?.tokens?.idToken?.payload?.['cognito:groups'];
      if (Array.isArray(groups)) setIsAdmin(groups.includes('Admin'));
      return idToken;
    } catch (err) {
      if (err instanceof Error && err.message.includes('Access to storage is not allowed')) {
        console.warn('LocalStorage is blocked. Fallback, etc...');
        return null;
      }
    }    
  }, []);

  const getUserID = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const userId = session?.tokens?.accessToken?.payload?.sub || null;
      setUserId(userId);
      return userId;
    } catch (error) {
      console.error('Error retrieving user ID:', error);
      return null;
    }
  }, []);
  
  // Fetch stories
  const fetchStories = useCallback(async (authToken: string) => {
    try {
      const response = await fetch(`${apiUrl}stories/get-active-stories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stories. Status: ${response.status}`);
      }

      const data = await response.json();
      setStories(data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  }, []);

  const fetchInProgressStories = useCallback(async (authToken: string, userId: string) => {
    try {
      const response = await fetch(`${apiUrl}stories/get-in-progress-stories?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch in-progress stories. Status: ${response.status}`);
      }

      const data = await response.json();
      setInProgressStories(data);
    } catch (error) {
      console.error('Error fetching in-progress stories:', error);
    }
  }, []);

  const fetchUserTokens = useCallback(async (authToken: string, userId: string) => {
    try {
      const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
  
      if (!response.ok) {
        throw new Error(`Failed to fetch user tokens. Status: ${response.status}`);
      }
  
      const data = await response.json();
      setTokens(data.token_balance || 0);
  
      // ✅ Check if the tutorial is completed
      if (!data.completed_tutorial) {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Tutorial' }],
        });
      }
    } catch (error) {
      console.error('Error fetching user tokens:', error);
    }
  }, [navigation]);
  

  // Initialize page logic
useFocusEffect(
  useCallback(() => {
    let isActive = true;
    setLoading(true); // Start loading when the page gains focus

    const initializePage = async () => {
      try {
        const authToken = await getAuthToken();
        const userId = await getUserID();

        if (authToken && userId && isActive) {
          await Promise.all([
            fetchStories(authToken),
            fetchInProgressStories(authToken, userId),
            fetchUserTokens(authToken, userId),
          ]);
        }
      } catch (error) {
        console.error('Error initializing page:', error);
      } finally {
        if (isActive) {
          setLoading(false); // Stop loading when requests are complete
        }
      }
    };

    initializePage();

    return () => {
      isActive = false; // Cleanup to prevent memory leaks
    };
  }, [getAuthToken, getUserID, fetchStories, fetchInProgressStories, fetchUserTokens])
);


  const convertAudioUrlToBase64 = async (audioUrl: string): Promise<string | null> => {
    try {
      if (Platform.OS === 'web') {
        console.log('Fetching audio from:', audioUrl);

        const response = await fetch(audioUrl, {
          method: 'GET',
          mode: 'cors', // Ensure CORS is enabled
          headers: {
            'Accept': 'audio/wav', // Adjust if using MP3
          },
        });

        if (!response.ok) {
          console.error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
          return null;
        }

        const arrayBuffer = await response.arrayBuffer();
        const base64String = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        return base64String;
      } else {
        // Mobile: Use expo-file-system
        const downloadResumable = FileSystem.createDownloadResumable(
          audioUrl,
          FileSystem.cacheDirectory + 'audio.wav'
        );

        const result = await downloadResumable.downloadAsync();

        if (result?.uri) {
          const base64Audio = await FileSystem.readAsStringAsync(result.uri, {
            encoding: FileSystem.EncodingType.Base64,
          });
          return base64Audio;
        } else {
          console.error('Failed to download the audio file');
          return null;
        }
      }
    } catch (error) {
      console.error('Error converting audio to Base64:', error);
      return null;
    }
  };


  const handleSelect = async (story: Story) => {
    resetContext();
    if (isSelectingStoryForUpdate) {
      navigation.navigate('UpdateStoryPage', { story });
      setIsSelectingStoryForUpdate(false);
      return;
    }

    if (story.status === 'COMPLETE') {
      console.log('Story complete - showing purchase modal');
      setSelectedStory(story);
      setShowPurchaseModal(true); // <<< Different modal
      return;
    }

    if (story.status === 'IN PROGRESS' && !story.character_selected) {
      console.log('Navigating to ExistingCharactersPage with params:', {
        sessionId: story.session_id,
        selectedStory: story,
        withImages: story.images,
      });
    
      // Use a timeout or separate step to ensure sequential updates
      setTimeout(() => {
        setContext({
          sessionId: story.session_id || '',
          selectedStory: story,
          withImages: story.images,
        });
    
        navigation.navigate('ExistingCharactersPage', {
          sessionId: story.session_id || '',
          selectedStory: story,
          withImages: story.images,
        });
      }, 0); // Timeout allows React to batch updates
      return;
    }
    
    

    if (story.status === 'IN PROGRESS') {
      try {
        const authToken = await getAuthToken();
        if (!authToken) throw new Error('Unable to retrieve auth token');

        const response = await fetch(
          `${apiUrl}story/get-last-message?session_id=${story.session_id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch last message. Status: ${response.status}`);
        }

        const data = await response.json();

        console.log('Last message data:', data);

        const audioBase64 = data.latest_audio
        ? await convertAudioUrlToBase64(data.latest_audio)
        : null;
        console.log(story.images)
        setContext({
          sessionId: story.session_id || '',
          storyData: story,
          characterDescription: undefined, // Add if applicable
          initialResponse: {
            response: data.latest_message?.content || '',
            audio: audioBase64 || '',
            session_id: story.session_id || '',
            image_url: data.image_url || null,
          },
          withImages: story.images,
        });
        
        navigation.navigate('StoryPage', {
          sessionId: story.session_id || '',
          storyData: story,
          withImages: story.images,
          initialResponse: {
            response: data.latest_message?.content || '',
            audio: audioBase64 || '',
            session_id: story.session_id || '',
            image_url: data.image_url || null,
          },
          characterDescription: undefined,
        });
      } catch (error) {
        console.error('Error fetching last message:', error);
      }
    } else {
      console.log('Setting selected story and showing modal');
      setSelectedStory(story);
      setShowModal(true);
    }
  };

  
  

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedStory(null);
  };

  const handleStorySelection = async (withImages: boolean, chapters: number) => {
    if (!selectedStory || !userId) return;
  
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');
  
      const response = await fetch(`${apiUrl}story/select-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id: userId,
          images: withImages,
          chapters,
          first_image: selectedStory.image_url,
          image_key: selectedStory.image_key,
          description: selectedStory.description,
          title: selectedStory.title,
          story_id: selectedStory.story_id,
          category: selectedStory.category,
          first_prompt: selectedStory.first_prompt,
          items: selectedStory.items,
          objective: selectedStory.objective
        }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to start session');
      }
  
      const data = await response.json();
  
      setShowModal(false); // Close the modal
      
      setContext({
        sessionId: data.session_id || '',
        selectedStory: selectedStory,
        withImages: withImages,
      });
      // Navigate to the next page with the selected `withImages` value
      navigation.navigate('ExistingCharactersPage', {
        selectedStory,
        withImages, // Pass the selected `withImages` value
        sessionId: data.session_id,
      });
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };
  
  

  const handleCreateNewStory = () => {
    navigation.navigate('CreateStoryPage');
  };

  const handleUpdateStory = () => {
    setIsSelectingStoryForUpdate(true);
  };

  const filteredStories = stories.filter((story) =>
    selectedCategory === 'All' || story.category === selectedCategory
  );

  const completedStories = inProgressStories.filter((story) =>
    (selectedCategory === 'All' || story.category === selectedCategory) && story.status === 'COMPLETE'
  );
  
  const filteredInProgressStories = inProgressStories.filter((story) =>
    (selectedCategory === 'All' || story.category === selectedCategory) && story.status === 'IN PROGRESS'
  );
  

  const popularStories = filteredStories.filter((story) => story.tags && story.tags.includes('Popular'));
  const newStories = filteredStories.filter((story) => story.tags && story.tags.includes('New'));
  const seasonalStories = filteredStories.filter((story) => story.tags && story.tags.includes('Seasonal'));

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Image source={require('./assets/wizard.gif')} style={styles.loadingGif} />
      </View>
    );
  }

  const handlePurchaseAttempt = () => {
    console.log('Showing parental gate for PDF');
    setGatePurpose('purchase_pdf');
    setShowParentalGate(true);
  };
  
  const handleParentalGateSuccess = async () => {
    console.log('✅ Parental gate passed');
    setShowParentalGate(false);

    if (gatePurpose === 'purchase_pdf') {
      await handlePurchasePdf();
    } else if (gatePurpose === 'subscription') {
      navigation.navigate('SubscriptionPage');
    }

    setGatePurpose(null);
  };


  const handleGateCancel = () => {
    setShowParentalGate(false);
  };

  const handlePurchasePdf = async () => {
    console.log('📦 Running handlePurchasePdf');
    if (!selectedStory || !userId) {
      console.warn('🚫 Missing selectedStory or userId', { selectedStory, userId });
      return;
    }
  
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');
  
      const response = await fetch(`${apiUrl}tokens/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          purchaseType: 'story_pdf_purchase',
          storyId: selectedStory.session_id,
          userId,
        }),
      });
  
      if (!response.ok) {
        console.error('❌ Stripe session creation failed:', response.status);
        return;
      }
  
      const data = await response.json();
      console.log('✅ Stripe checkout session:', data);
  
      if (data.checkoutUrl) {
        if (Platform.OS === 'web') {
          window.open(data.checkoutUrl, '_blank');
        } else {
          Linking.openURL(data.checkoutUrl);
        }
      } else {
        throw new Error('No checkout URL in response');
      }
  
      setShowPurchaseModal(false);
      setSelectedStory(null);
    } catch (error) {
      console.error('❌ Stripe Checkout error:', error);
    }
  };
  
  

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <View style={{ flex: 1, position: 'relative' }}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => {
              navigation.navigate('SettingsPage');
            }}
            style={styles.iconButton}
          >
            <Image source={require('./assets/settings-icon.png')} style={styles.icon} />
          </TouchableOpacity>
          <View style={styles.headerRight}>
            <View style={styles.tokenContainer}>
              <TouchableOpacity
                onPress={() => {
                  if (Platform.OS === 'android') {
                    console.log('🔒 Showing parental gate for subscription');
                    setGatePurpose('subscription');
                    setShowParentalGate(true);
                  } else {
                    navigation.navigate('SubscriptionPage');
                  }
                }}
                style={styles.iconButton}
              >
                <Image source={require('./assets/tokens.png')} style={styles.icon} />
              </TouchableOpacity>
              <Text style={styles.tokenCount}>{tokens}</Text>
            </View>
            {isAdmin && (
            <View style={styles.buttonContainer}>            
              <TouchableOpacity
                style={styles.createButton}
                onPress={handleCreateNewStory}
              >
                <Text style={styles.buttonText}>New Story</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.updateButton}
                onPress={handleUpdateStory}
              >
                <Text style={styles.buttonText}>Update Story</Text>
              </TouchableOpacity>
            </View>
          )}
          </View>
        </View>
        <SafeAreaView style={styles.safeArea}>
        <StatusBar backgroundColor="#F7F8FB" barStyle="dark-content" />
        <ScrollView
          contentContainerStyle={{
            ...styles.storiesPage,
            overflow: 'visible',
          }}
          keyboardShouldPersistTaps="handled"
        >

        <View style={styles.sectionContainer}>
        <Text style={styles.sectionTitle}>Categories</Text>
          {/* Horizontal Scrollable Categories */}
          <View style={styles.allDoneFlexGrow}>
          <ScrollView
            horizontal={true}
            contentContainerStyle={[
              styles.categoriesContainer,
              { flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }, // Prevent stretching and shrinking
            ]}
            showsHorizontalScrollIndicator={false}
          >
          {['All', 'Mythical', 'Medieval', 'Whimsical', 'Adventure'].map((category) => (
            <TouchableOpacity
              key={category}
              style={[styles.categoryBtn, selectedCategory === category ? styles.activeCategoryBtn : null]}
              onPress={() => {
                console.log(`Category ${category} selected`);
                setSelectedCategory(category);
              }}
            >
              <Text style={[
                styles.categoryBtnText,
                selectedCategory === category && styles.activeCategoryBtnText
              ]}>
                {category}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        </View>
        {completedStories.length > 0 && (
          <StorySection
            title="Completed Stories"
            stories={completedStories}
            handleSelect={handleSelect}
            showProgressBar={false}
          />
        )}
        <StorySection title="In Progress" stories={filteredInProgressStories} handleSelect={handleSelect} showProgressBar />
        <StorySection title="Popular Stories" stories={popularStories} handleSelect={handleSelect} showProgressBar={false} />
        <StorySection title="New Stories" stories={newStories} handleSelect={handleSelect} showProgressBar={false} />
        <StorySection title="Seasonal Stories" stories={seasonalStories} handleSelect={handleSelect} showProgressBar={false} />
        </View>
        {showModal && selectedStory ? (
        <Modal
          isVisible={showModal}
          onClose={handleModalClose}
          onSelectOption={handleStorySelection} // Pass the function to handle selection
          tokenBalance={tokens}
          userId={userId}
        />
      ) : null}
      {showPurchaseModal && selectedStory && (
      <PurchaseStoryModal
        isVisible={showPurchaseModal}
        onClose={() => {
          setShowPurchaseModal(false); // Only close the modal
        }}
        onPurchase={handlePurchaseAttempt}
        title={selectedStory.title}
      />  
    )}
      <ParentalGateModal
        isVisible={showParentalGate}
        onSuccess={handleParentalGateSuccess} // <- correctly defined
        onCancel={handleGateCancel}
      />
      </ScrollView>
      </SafeAreaView>
      </View>
    </GestureHandlerRootView>
  );
};

// Story Section Component
const StorySection: React.FC<{ title: string; stories: Story[]; handleSelect: (story: Story) => void; showProgressBar?: boolean }> = ({
  title,
  stories,
  handleSelect,
  showProgressBar = true,
}) => {
  return (
    <View style={styles.storySection}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <ScrollView
          horizontal
          contentContainerStyle={{
            flexDirection: 'row',
            marginLeft: 20,
          }}
        >
          {stories.length > 0 ? (
            stories.map((story, index) => {
              const progressPercentage = Math.min(100, (story.prompt_number / (story.chapters * 7)) * 100);
              const backgroundColor = index % 2 === 0 ? '#8690A7' : '#AFC9BE';

              return (
                <TouchableOpacity
                  key={story.story_id}
                  style={[styles.storyCard, { backgroundColor }]}
                  onPress={() => handleSelect(story)}
                >
                  <Image source={{ uri: story.image_url }} style={styles.storyImage} />
                  
                  {/* Show progress bar only if showProgressBar is true */}
                  {showProgressBar && (
                    <View style={styles.progressBarContainer}>
                      <View style={[styles.progressBar, { width: `${progressPercentage}%` }]} />
                    </View>
                  )}

                  <View style={styles.storyInfo}>
                    <Text style={styles.storyTitle}>{story.title}</Text>
                    <Text style={styles.storyDescription}>{story.description}</Text>
                  </View>
                </TouchableOpacity>
              );
            })
          ) : (
            <Text>No {title.toLowerCase()} found.</Text>
          )}
        </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1, // This should be retained for proper layout
    backgroundColor: '#F7F8FB',
    overflow: 'hidden', // Prevent unwanted overflow behaviors
    paddingTop: 40,
  },
  storiesPage: {
    padding: 20,
    paddingBottom: 100,
    backgroundColor: '#F7F8FB',
    paddingTop: 60,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Dim background
  },
  loadingGif: {
    width: 150,
    height: 150,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    height: 60, // Pick a height that matches your design
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  iconButton: {
    padding: 8,
  },
  icon: {
    width: 24,
    height: 24,
  },
  headerRight: {
    flexDirection: 'row',
  },
  tokenContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tokenCount: {
    marginRight: 10,
    color: '#534466',
    fontFamily: 'Futura',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  createButton: {
    backgroundColor: '#4CAF50',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  updateButton: {
    backgroundColor: '#FFA500',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  allDoneFlexGrow: {
    maxHeight: 50,
  },
  categoriesContainer: {
    paddingRight: 10, // Adds margin on both sides for scrolling
    alignItems: 'center',
    flexGrow: 0, // Explicitly disable flexGrow
    flexShrink: 0, // Prevent shrinking if necessary
    flexBasis: 'auto', // Reset any unintended stretching
    marginLeft: 20,
  },
  categoryBtn: {
    backgroundColor: '#e0e0e0',
    padding: 10,
    borderRadius: 20,
    marginRight: 10,
  },
  activeCategoryBtn: {
    backgroundColor: '#534466',
  },
  categoryBtnText: {
    fontFamily: 'Futura',
  },
  activeCategoryBtnText: {
    fontFamily: 'Futura',
    color: '#F7F8FB'
  },
  storySection: {
    marginVertical: 20,
    backgroundColor: '#F7F8FB',
    width: Math.min((screenWidth*.95), 800),      // Allows shrinking on smaller screens
  },
  sectionContainer: {
    alignSelf: 'center',
  },
  sectionTitle: {
    fontSize: 30,
    fontWeight: 'bold',
    marginBottom: 20,
    fontFamily: 'Futura',
    marginLeft: 20,
  },
  storyCard: {
    width: 200,
    marginRight: 10,
    borderRadius: 10,
  },
  storyImage: {
    width: '100%',
    height: 150,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  progressBarContainer: {
    height: 5,
    backgroundColor: '#d9d9d9',
    borderRadius: 4,
    margin: 5,
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#544564',
  },
  storyInfo: {
    padding: 20,
  },
  storyTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    fontFamily: 'Futura',
  },
  storyDescription: {
    fontSize: 14,
    marginVertical: 5,
    fontFamily: 'Futura',
  },
  storyFooter: {
    flexDirection: 'row',
    fontFamily: 'Futura',
    marginTop: 5,
  },
  chapterIcon: {
    height: 16,
    width: 16,
    justifyContent: 'center',
    marginRight: 10,
  },
  chaptersText: {
    fontFamily: 'Futura',
  },
  dropdownIcon: {
    width: 25,
    height: 25,
    marginLeft: 10, // Proper spacing for the icon
  },  
  dropdownOption: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#D9D9D9',
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 6,
    width: '100%', // ensures it matches the dropdown width
  },
  dropdownOptionText: {
    fontFamily: 'Futura',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: 'bold',
    color: "#6C6C6C",
  },
});

export default StoriesPage;
